import { useState } from 'react'

import { SidebarProvider } from '@/components/ui/sidebar'
import { cn } from '@/lib/utils'
import { AccountContent } from './AccountContent'
import { DEFAULT_ORG_NAME, PanelSide, Viewport } from './accountModel'
import { AccountTopNav } from './AccountTopNav'
import { PrototypeSidebar } from './PrototypeSidebar'

interface AgentPanel {
  side: PanelSide
  content: React.ReactNode
}

interface AccountShellProps {
  viewport: Viewport
  /** Slot above the product tabs in the side nav. */
  sidebarLead?: React.ReactNode
  /** Slot centred in the top nav — Muse's placement for the agent. */
  topNavCenter?: React.ReactNode
  /** Agent docked beside the page, pushing it aside rather than covering it. */
  panel?: AgentPanel
  /** Agent laid over the page, covering what is underneath. */
  overlay?: React.ReactNode
  /** Tab bar pinned to the bottom edge — a mobile placement. */
  bottomNav?: React.ReactNode
  orgName?: string
  className?: string
}

/**
 * The customer's account, forked for this prototype: production's arrangement
 * with a slot at every place the agent could live. An option is a choice of
 * slots, so the options stay comparable and none of them touches the live app.
 */
export function AccountShell({
  viewport,
  sidebarLead,
  topNavCenter,
  panel,
  overlay,
  bottomNav,
  orgName = DEFAULT_ORG_NAME,
  className,
}: AccountShellProps) {
  const mobile = viewport === Viewport.MOBILE
  // Null is the dashboard, which the carrier name in the nav returns to.
  const [page, setPage] = useState<string | null>(null)

  return (
    // The real nav components read sidebar context; min-h-0 overrides the provider's
    // min-h-svh, which would otherwise stretch every pane to the viewport.
    <SidebarProvider
      className={cn('border-ink-100 block min-h-0 overflow-hidden border bg-white', className)}
    >
      <div className="flex h-full">
        {!mobile && (
          <PrototypeSidebar orgName={orgName} lead={sidebarLead} page={page} onNavigate={setPage} />
        )}
        {panel?.side === PanelSide.LEFT && <div className="shrink-0">{panel.content}</div>}

        <div className="flex min-w-0 flex-1 flex-col">
          <AccountTopNav viewport={viewport} center={topNavCenter} />
          {/* The overlay's containing block, so it covers the page and not the chrome. */}
          <div className="relative min-h-0 flex-1 overflow-hidden">
            <AccountContent viewport={viewport} page={page} />
            {overlay}
          </div>
          {bottomNav}
        </div>

        {panel?.side === PanelSide.RIGHT && <div className="shrink-0">{panel.content}</div>}
      </div>
    </SidebarProvider>
  )
}
